/**
 * The durable store behind the offline write queue — **IndexedDB, and an in-memory double**.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE IS, AND WHAT IT DELIBERATELY IS NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Spec 10 §2.1 gives the phone four IndexedDB stores — `events`, `notes`, `day`, `meta`.
 * **This file builds two of them and says so**: the queue of unsent writes (§2.1's `events`,
 * narrowed to the writes this build can actually make) and the `meta` record that holds the
 * device id. The cached day payload and the full materialised `notes` view are NOT here:
 * they belong with the service worker and the pull cursor, neither of which is built, and a
 * half-populated local mirror that the app read as though it were complete would be exactly
 * the kind of claim spec 10 §1 exists to forbid.
 *
 * Everything unsent is nevertheless visible on screen, because the queue itself carries the
 * full note payload — see `noteQueue.ts` and the merge in `CapturePanel`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE PLACE THIS DIFFERS FROM `credentialStore.ts`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The credential store swallows every storage failure, because a credential that does not
 * survive a reload costs one sign-in. **This store must not.** A failed write here is the
 * single way an observation could genuinely be lost, and spec 10 Q-10c is explicit that it
 * "must be surfaced loudly rather than swallowed". So {@link QueueStorage.put} rejects, the
 * engine keeps the write in memory, and the UM is told in words that the note is not safe on
 * the phone. Reads and deletes still fail soft — neither can lose anything.
 *
 * Its own database, `hockeytd-queue`, rather than a second store inside `hockeytd`: adding a
 * store to an existing database means a version bump, and a version bump blocks on every open
 * connection the credential store is holding. Two databases have no such coupling.
 */

import type { CaptureNoteRequest } from "./types.js";

export const QUEUE_DB_NAME = "hockeytd-queue";
export const QUEUE_STORE_NAME = "writes";
export const QUEUE_META_STORE_NAME = "meta";
export const QUEUE_DEVICE_ID_KEY = "deviceId";

/* ── The shape of a queued write ──────────────────────────────────────────── */

interface QueuedWriteBase {
  /**
   * The monotonic client sequence number of spec 10 §2.1 — the record's key **and** its
   * order. Replay is strictly ascending, because a capture must reach the server before the
   * lock or the release that names the same note. Never reused, never re-sorted.
   */
  seq: number;
  queuedAt: string;
  /** How many times the drain has tried and been unable to reach the server. Drives back-off. */
  attempts: number;
  lastError: string | null;
}

/** `PUT /notes/:id` — the note travels whole, so an unsent note is still renderable. */
export interface QueuedCapture extends QueuedWriteBase {
  kind: "capture";
  noteId: string;
  note: CaptureNoteRequest;
}

/** `POST /notes/:id/lock` — ruling §H's explicit act, waiting its turn. */
export interface QueuedLock extends QueuedWriteBase {
  kind: "lock";
  noteId: string;
}

/**
 * `POST /notes/release` — the press, whole. `unlock` travels with it, so the §H
 * acknowledgement the author gave to the warning is the one that reaches the API however
 * long the queue holds it.
 */
export interface QueuedRelease extends QueuedWriteBase {
  kind: "release";
  matchId: number;
  notes: string[];
  unlock: string[];
}

/**
 * `POST /um/umpires/:id/final-push` — the Report Studio's single press (um-app/06 §4.5),
 * queued as **ONE unit**.
 *
 * One entry, never two, and that is the whole reason it is a queued kind of its own rather
 * than a pair of pushes: a press that half-queued would drain as two acts, could half-drain,
 * and would put a marks push in TMS under a press the UM would have to make again. One row,
 * one drain, one answer.
 *
 * `pressId` is minted when the press is made and travels with the entry, so however long the
 * phone holds it the server sees the SAME press id — and a replay after a drain that was cut
 * off mid-answer returns the stored receipts instead of sending twice (migration 026).
 */
export interface QueuedFinalPush extends QueuedWriteBase {
  kind: "finalPush";
  tmsPersonId: string;
  pressId: string;
}

export type QueuedWrite = QueuedCapture | QueuedLock | QueuedRelease | QueuedFinalPush;

/** Every note a queued write concerns — the source of the per-note pending marks (§4). */
/**
 * The notes a queued write concerns — what the sync sheet lists and `depth` counts.
 *
 * A final push concerns **no note at all**: it is a press about an umpire's marks and report,
 * not an observation. It returns an empty list rather than an invented id, so it cannot appear
 * in a per-note list or be counted as a note waiting to send. It is still in `writes`, still
 * drains in order, and still keeps the queue non-empty — so the sync chip cannot read `synced`
 * while a press is waiting.
 */
export function notesOf(write: QueuedWrite): readonly string[] {
  if (write.kind === "release") return write.notes;
  if (write.kind === "finalPush") return [];
  return [write.noteId];
}

/* ── The seam ─────────────────────────────────────────────────────────────── */

export interface QueueStorage {
  /** Every unsent write, ascending by `seq`. Fails soft: an unreadable store reads empty. */
  list: () => Promise<QueuedWrite[]>;
  /** **Rejects on failure.** The one operation whose failure could lose an observation. */
  put: (write: QueuedWrite) => Promise<void>;
  /** Fails soft — a write that cannot be deleted was already delivered. */
  remove: (seq: number) => Promise<void>;
  /** This device's id, minted once and kept (spec 10 §2.1's `meta`). Fails soft. */
  deviceId: (mint: () => string) => Promise<string>;
}

/* ── The real implementation: raw IndexedDB, no library ───────────────────── */

function hasIndexedDb(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(QUEUE_DB_NAME, 1);
    request.onupgradeneeded = (): void => {
      const database = request.result;
      if (!database.objectStoreNames.contains(QUEUE_STORE_NAME)) {
        database.createObjectStore(QUEUE_STORE_NAME, { keyPath: "seq" });
      }
      if (!database.objectStoreNames.contains(QUEUE_META_STORE_NAME)) {
        database.createObjectStore(QUEUE_META_STORE_NAME);
      }
    };
    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(request.error ?? new Error("The note queue could not be opened."));
    };
    request.onblocked = (): void => {
      reject(new Error("The note queue is blocked by another open version of this app."));
    };
  });
}

function runTransaction<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(storeName, mode);
        const request = work(transaction.objectStore(storeName));
        request.onsuccess = (): void => {
          resolve(request.result);
        };
        request.onerror = (): void => {
          reject(request.error ?? new Error("The note queue refused the operation."));
        };
        transaction.oncomplete = (): void => {
          database.close();
        };
        transaction.onabort = (): void => {
          database.close();
          reject(transaction.error ?? new Error("The note queue aborted."));
        };
      }),
  );
}

/** The browser store. See the header for which operations fail loudly and which fail soft. */
export function indexedDbQueueStorage(): QueueStorage {
  return {
    list: async (): Promise<QueuedWrite[]> => {
      if (!hasIndexedDb()) return [];
      try {
        const rows = await runTransaction<unknown[]>(QUEUE_STORE_NAME, "readonly", (store) =>
          store.getAll(),
        );
        return (rows as QueuedWrite[]).slice().sort((left, right) => left.seq - right.seq);
      } catch {
        return [];
      }
    },
    put: async (write: QueuedWrite): Promise<void> => {
      // NOT swallowed, and not silently skipped either. A browser with no IndexedDB cannot
      // promise durability, and the honest thing is to say so rather than to return as
      // though the note were safe on the phone.
      if (!hasIndexedDb()) {
        throw new Error("This browser has no storage for notes that are waiting to send.");
      }
      await runTransaction(QUEUE_STORE_NAME, "readwrite", (store) => store.put(write));
    },
    remove: async (seq: number): Promise<void> => {
      if (!hasIndexedDb()) return;
      try {
        await runTransaction(QUEUE_STORE_NAME, "readwrite", (store) => store.delete(seq));
      } catch {
        // A write that cannot be deleted has already been delivered; the worst case is one
        // idempotent replay, which the server treats as a no-op by construction.
      }
    },
    deviceId: async (mint: () => string): Promise<string> => {
      if (!hasIndexedDb()) return mint();
      try {
        const held = await runTransaction<unknown>(QUEUE_META_STORE_NAME, "readonly", (store) =>
          store.get(QUEUE_DEVICE_ID_KEY),
        );
        if (typeof held === "string" && held !== "") return held;
        const minted = mint();
        await runTransaction(QUEUE_META_STORE_NAME, "readwrite", (store) =>
          store.put(minted, QUEUE_DEVICE_ID_KEY),
        );
        return minted;
      } catch {
        // A device id that does not survive a reload identifies which device wrote a note
        // slightly less well. Nothing is lost, so this one fails soft.
        return mint();
      }
    },
  };
}

/* ── The in-memory double, for tests ──────────────────────────────────────── */

export interface MemoryQueueStorage extends QueueStorage {
  /** Synchronous inspection, so a test can assert what is durable without awaiting. */
  peek: () => QueuedWrite[];
  /** Seed writes that were on the phone before this render — the force-quit scenario (§5.6). */
  seed: (writes: readonly QueuedWrite[]) => void;
  /**
   * Make the next `put` reject, so the one failure mode that could genuinely lose a note is
   * testable (Q-10c). Cleared after it fires.
   */
  failNextPut: (message?: string) => void;
}

export function memoryQueueStorage(initial: readonly QueuedWrite[] = []): MemoryQueueStorage {
  let held: QueuedWrite[] = initial.map((write) => ({ ...write }));
  let device: string | null = null;
  let failure: string | null = null;

  return {
    list: (): Promise<QueuedWrite[]> =>
      Promise.resolve(held.slice().sort((left, right) => left.seq - right.seq)),
    put: (write: QueuedWrite): Promise<void> => {
      if (failure !== null) {
        const message = failure;
        failure = null;
        return Promise.reject(new Error(message));
      }
      held = [...held.filter((existing) => existing.seq !== write.seq), { ...write }];
      return Promise.resolve();
    },
    remove: (seq: number): Promise<void> => {
      held = held.filter((existing) => existing.seq !== seq);
      return Promise.resolve();
    },
    deviceId: (mint: () => string): Promise<string> => {
      device ??= mint();
      return Promise.resolve(device);
    },
    peek: (): QueuedWrite[] => held.slice().sort((left, right) => left.seq - right.seq),
    seed: (writes: readonly QueuedWrite[]): void => {
      held = writes.map((write) => ({ ...write }));
    },
    failNextPut: (message = "The phone refused to store this note."): void => {
      failure = message;
    },
  };
}
